import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, X, ZoomIn } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const POINTS = [
  "Compressed, absorbent 100% cotton — sized specifically for the Mi Binky chamber",
  "Sterile and individually wrapped for everyday hygiene",
  "The correct fit, every time — no trimming or guesswork",
  "Swapped in seconds using the supplied precision tweezers",
];

const ADDONS_ALT =
  "BINKY.LIFE Add-Ons — sterile cotton strip refills box, comfort drops collection and 15 ml bottles";

export const CottonInserts = () => {
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <section
      id="cotton"
      data-testid="cotton-section"
      className="relative overflow-hidden bg-gradient-to-b from-white via-binky-greenlight/60 to-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <div className="relative mx-auto h-[380px] w-full max-w-[520px] sm:h-[440px]" data-testid="cotton-visual">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -6 }}
              whileInView={{ opacity: 1, y: 0, rotate: -4 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ rotate: -2, scale: 1.02 }}
              className="absolute left-0 top-0 w-[86%] overflow-hidden rounded-3xl border border-slate-100 bg-white p-3 shadow-xl shadow-binky-blue/10"
            >
              <img
                src="/assets/cotton-front.png"
                alt="BINKY.LIFE Sterile Cotton Strip — front of pack"
                loading="lazy"
                className="w-full object-contain"
                data-testid="cotton-front-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 6 }}
              whileInView={{ opacity: 1, y: 0, rotate: 3 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ rotate: 1, scale: 1.02 }}
              className="absolute bottom-0 right-0 w-[72%] overflow-hidden rounded-3xl border border-slate-100 bg-white p-3 shadow-2xl shadow-binky-green/20"
            >
              <img
                src="/assets/cotton-back.png"
                alt="BINKY.LIFE Sterile Cotton Strip — sterile, 100% pure cotton"
                loading="lazy"
                className="w-full object-contain"
                data-testid="cotton-back-image"
              />
            </motion.div>
            <motion.span
              initial={{ opacity: 0, scale: 0.6 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute -right-2 top-6 rounded-full bg-binky-green px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-binky-green/30"
              data-testid="cotton-refill-badge"
            >
              Refill packs available
            </motion.span>
          </div>

          <Reveal>
            <Eyebrow className="text-binky-green">Cotton Inserts</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Fresh cotton inserts <span className="text-binky-green">when you need them.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
              Mi Binky uses compressed, absorbent cotton inserts engineered specifically for the
              internal chamber. When it&apos;s time for a fresh one, BINKY.LIFE refill packs keep you
              stocked — convenient, correctly sized and effortless to replace.
            </p>
            <ul className="mt-8 space-y-4">
              {POINTS.map((p, i) => (
                <Reveal key={p} delay={0.1 + i * 0.08}>
                  <li className="flex items-start gap-3 text-sm font-semibold text-slate-700 md:text-base">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-binky-green/15 text-binky-green">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {p}
                  </li>
                </Reveal>
              ))}
            </ul>
            <button
              data-testid="cotton-register-button"
              onClick={() => scrollToId("#register")}
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-binky-green px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-binky-green/30 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Register for refill availability
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>

        <Reveal className="mt-16" data-testid="addons-block">
          <div className="text-center">
            <Eyebrow className="justify-center text-binky-green">Mi Binky Add-Ons</Eyebrow>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
              Refills and comfort drops, designed to work together — tap the artwork to view it
              full-size.
            </p>
          </div>
          <button
            data-testid="addons-image-button"
            onClick={() => setLightbox(true)}
            className="group relative mx-auto mt-8 block w-full max-w-2xl cursor-zoom-in overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_24px_60px_-24px_rgba(27,79,160,0.3)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_36px_72px_-24px_rgba(111,191,68,0.35)]"
            aria-label="View the Mi Binky Add-Ons artwork full-size"
          >
            <img
              src="/assets/addons-collection.webp"
              alt={ADDONS_ALT}
              loading="lazy"
              data-testid="addons-image"
              className="h-auto w-full object-contain"
            />
            <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-binky-navy/85 px-4 py-2 text-xs font-bold text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
              <ZoomIn className="h-3.5 w-3.5" />
              Tap to view full-size
            </span>
          </button>
        </Reveal>
      </div>

      {lightbox && (
        <div
          data-testid="addons-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-binky-navy/90 p-4 backdrop-blur-md sm:p-8"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Mi Binky Add-Ons artwork — full-size view"
        >
          <button
            data-testid="addons-lightbox-close"
            onClick={() => setLightbox(false)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-binky-pink"
            aria-label="Close full-size artwork"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src="/assets/addons-collection.webp"
            alt={ADDONS_ALT}
            className="max-h-[92vh] max-w-[94vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};
